import { Button, Grid, Tab, Tabs, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import jsonpath from 'jsonpath';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { DataAPIContext, UPDATE } from '../api';
import { DELETE_BATCH } from '../api/dataProvider';
import AccessDeniedView from '../components/AccessDeniedView';
import BlockRedirect from '../components/BlockRedirect';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import { getComparableState } from './tripComparableState';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import NavigateBackBar from '../components/NavigateBackBar';
import PageMeta from '../components/PageMeta';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import TabPanel from '../components/TabPanel';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import TripBuilder from './TripBuilder';
import TripChatList from './TripChatList';
import TripEditInfo from './TripEditInfo';
import TripEditItems from './TripItemList';
import TripFloorplans from './TripFloorplans';
import TripLineSet from './TripLineSet';
import TripLoading from './TripLoading';
import TripPreCheckSettings from './TripPreCheckSettings';
import UserManager from './UserManager';
import { addTransportationToAllActivities, checkTripItemsVisibility, ConfirmationDialogText, hideIfTimeLabelTextNull, reformatCustomIds, removeEmptyActivities, removeTransportationToggle, removeUnusedTagsFromContextFields, TRIP_TYPE_BUS, TRIP_TYPE_EXHIBITION, TRIP_TYPE_TOUR, validateRequiredTripConditions } from './utils';

const useStyles = makeStyles((theme) => ({
    gridRoot: {
        backgroundColor: '#FCFCFE',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 6rem)`,
        },
    },
    tabSelectButtons: {
        border: 'solid 1px #D8D8D8',
        margin: '0 1rem 0rem 1rem !important',
        borderRadius: '12px 12px 0px 0px',
        backgroundColor: '#FFFFFF',
        [theme.breakpoints.down('sm')]: {
            margin: '0 !important',
        },
    },
    paper: {
        borderRadius: '12px',
    },
    mainButtonsWrapper: {
        padding: '1rem',
        backgroundColor: '#F5F5F5',
        zIndex: 1000,
        position: 'sticky',
        bottom: 0,
        left: 0,
    },
    RevisionHistoryDialogTabPanel: {
        height: `calc(${theme.cp.contentHeight} - 6rem)`,
        marginTop: '-1rem',
        overflowX: 'hidden'
    }
}));

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}


// Map own-fields from backend to UI-facing fields for editing
const mapOwnFieldsToUI = (trip) => {
    if (!trip || typeof trip !== 'object') return trip;
    const mapped = { ...trip };
    if (Object.prototype.hasOwnProperty.call(trip, 'has_group_chat_own')) {
        mapped.has_group_chat = trip.has_group_chat_own;
    }
    if (Object.prototype.hasOwnProperty.call(trip, 'location_sharing_own')) {
        mapped.location_sharing = trip.location_sharing_own;
    }
    // Keep tag_categories as the full effective list (including inherited operator defaults)
    // for display in dropdowns. tag_categories_own is available for editing own categories.
    // Expose effective pre_check_settings (read-only) via pre_check_settings,
    // and own editable via pre_check_settings_own already present in payload.
    // No mapping required here besides preserving as-is; saving keeps sending to pre_check_settings.
    return mapped;
};

const checkAdditionalCustomIds = async (currentTrip, setClientErrors, enqueueSnackbar) => {
    let _trip = currentTrip;
    let error = false;
    if (_trip.additional_custom_ids && _trip.additional_custom_ids.length > 0) {
        for (let index = 0; index < _trip.additional_custom_ids.length; index++) {
            const cid = _trip.additional_custom_ids[index].custom_id;
            if (cid == null || String(cid).trim() === '') {
                error = true;
            }
        }
    }
    if (setClientErrors) {
        setClientErrors((prev) => ({ ...prev, missingAdditionalCustomIDsValues: error }));
    }
    if (error && enqueueSnackbar) {
        enqueueSnackbar('External IDs must not be empty.', { variant: 'error' });
    }
    return error;
};

const TripEditForm = (props) => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const urlParams = useParams();

    const [isTouched, setIsTouched] = React.useState(false);

    const [trip, setTrip] = React.useState(mapOwnFieldsToUI(reformatCustomIds(props.data)));
    // eslint-disable-next-line no-unused-vars
    const [validationState, setValidationState] = React.useState({});
    const [originalState, setOriginalState] = React.useState(getComparableState(mapOwnFieldsToUI(reformatCustomIds(props.data))));
    const [syncActive, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [showDefaultsModal, setShowDefaultsModal] = React.useState(false);
    const prevUseDefaultsRef = React.useRef(!!props.data?.use_operator_defaults);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm, tourOperator } = React.useContext(ContextStore);

    const tripType = trip.trip_type ?? TRIP_TYPE_TOUR;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const visibleTabs = React.useMemo(() => {
        const tabs = [{ key: 'info', label: 'Trip Info' }];
        if (tripType === TRIP_TYPE_TOUR)
            tabs.push({ key: 'itinerary', label: 'Itinerary' });
        if (tripType === TRIP_TYPE_BUS)
            tabs.push({ key: 'lineset', label: 'Line Set' });
        if (tripType === TRIP_TYPE_EXHIBITION)
            tabs.push({ key: 'floorplans', label: 'Floorplans' });
        if (hasPerm('tour_participant', 'list'))
            tabs.push({ key: 'users', label: 'Users' });
        tabs.push({ key: 'items', label: 'Trip Items' });
        tabs.push({ key: 'checkin', label: 'Pre-check-in' });
        // Chat channels open into /chat/<channel>/conversations, which is empty for a user
        // without chat access (the chat list is gated on being a conversation participant).
        // Same gate the sidebar's Chat item uses (layout/menu.js), so the tab can't hand a
        // no-chat user a dead-end link -- or the Unarchive button next to it (CP-8142).
        if (hasPerm('chat', 'list'))
            tabs.push({ key: 'chat', label: 'Chat Channels' });
        if (hasPerm('tour', 'view_history'))
            tabs.push({ key: 'changes', label: 'Change history' });
        return tabs;
    }, [tripType, hasPerm]);

    const paramsToTab = React.useMemo(() => {
        const m = {};
        visibleTabs.forEach((t, i) => { m[t.key] = i; });
        return m;
    }, [visibleTabs]);

    const tabToParams = React.useMemo(() => {
        const m = {};
        visibleTabs.forEach((t, i) => { m[i] = t.key; });
        return m;
    }, [visibleTabs]);

    const currentTab = paramsToTab[urlParams.tab] !== undefined ? paramsToTab[urlParams.tab] : 0;

    const currentState = getComparableState(trip);
    const hasChanged = originalState !== currentState;

    //There are css conflicts when popper is activated, e.g buttons start hovering on mouse scroll
    const [activePopperState, setActivePopperState] = React.useState(false);
    const [newDocsAdded, setNewDocsAdded] = React.useState(false);

    const [clientErrors, setClientErrors] = React.useState({
        missingAdditionalCustomIDsValues: false,
        amountsNotEntered: false,
        missingWeeklySchedule: false,
        invalidURLReview: false,
        invalidURLPhotoSharing: false,
        hasEmptyCheckinOrBoardingSelection: false,
        isMissingDeparture: false,
        isMissingBoarding: false,
    });

    const tripTitleLength = isSmallScreen ? 40 : 120;

    // Sync state when props.data changes to prevent false positive hasChanged
    React.useEffect(() => {
        const reformattedData = reformatCustomIds(props.data);
        const mapped = mapOwnFieldsToUI(reformattedData);
        setTrip(mapped);
        setOriginalState(getComparableState(mapped));
        prevUseDefaultsRef.current = !!mapped.use_operator_defaults;
    }, [props.data]);

    // Show modal only when toggled ON and defaults are missing
    React.useEffect(() => {
        const prev = prevUseDefaultsRef.current;
        const curr = !!trip.use_operator_defaults;
        if (!prev && curr) {
            const hasOperatorTagDefaults = (tourOperator?.default_tag_categories?.length ?? 0) > 0;
            const hasConfigDefaults =
                tourOperator?.config?.defaults &&
                Object.keys(tourOperator.config.defaults).length > 0;
            const missing = !hasOperatorTagDefaults && !hasConfigDefaults;
            if (missing) setShowDefaultsModal(true);
        }
        prevUseDefaultsRef.current = curr;
    }, [trip.use_operator_defaults, tourOperator]);

    const onTripInfoChange = React.useCallback((newTrip) => setTrip(newTrip), []);
    const onCrosssellsChange = (crosssells) => setTrip((prev) => ({ ...prev, crosssells }));

    const handleTabChange = (ev, newTab) => {
        const key = tabToParams[newTab];
        if (key) {
            history.push(`/trip/${urlParams.id}/${key}`);
        }
    };

    const onTripFlowChange = (update) => {
        setTrip((prevState) => {
            const ns = { ...prevState };
            let hasUploads = false;
            for (const k in update) {
                if (!Object.prototype.hasOwnProperty.call(update, k)) {
                    continue;
                }
                if (update[k] && update[k].constructor === File && k.endsWith('_')) {
                    // Files should be in the root object to be processed correctly.
                    // The convention is that if the value is a File instance AND the identifier ends with _,
                    // that file is put into the root object (last chunk of the identifier is used as key, _ is stripped)
                    const p = jsonpath.parse(`$.${k}`);
                    const nk = p[p.length - 1].expression.value;
                    const nks = nk.substr(0, nk.length - 1);

                    ns[nks] = update[k];
                    hasUploads = true;
                } else {
                    jsonpath.value(ns, `$.${k}`, update[k]);
                }
                if (hasUploads) {
                    ns._upload = true;
                }
            }
            return ns;
        });
    };

    const onValidate = useCallback((key, vCanSave) => {
        setValidationState(prevState => {
            const ns = { ...prevState };
            if (vCanSave === null && typeof vCanSave === 'object') {
                // null is passed, remove the key instead of merging the value into the state
                delete ns[key];
            } else {
                ns[key] = vCanSave;
            }
            return ns;
        });
    }, []);

    const stripUIFieldsFromItinerary = (itinerary) => {
        if (!itinerary || !Array.isArray(itinerary.days)) return itinerary;
        const newItinerary = {
            ...itinerary, days: itinerary.days.map((day) => {
                const newDay = { ...day };
                if (Array.isArray(newDay.timeslots)) {
                    newDay.timeslots = newDay.timeslots.map((ts) => {
                        const nts = { ...ts };
                        // Remove UI-only fields
                        Object.keys(nts)
                            .filter((k) => k === 'open' || k.startsWith('_'))
                            .forEach((k) => delete nts[k]);
                        return nts;
                    });
                }
                return newDay;
            })
        };
        return newItinerary;
    };

    const normalizePinsInItinerary = (itinerary) => {
        if (!itinerary || !Array.isArray(itinerary.days)) return itinerary;
        const ni = {
            ...itinerary, days: itinerary.days.map((day) => {
                const nd = { ...day };
                if (Array.isArray(nd.timeslots)) {
                    nd.timeslots = nd.timeslots.map((ts) => {
                        const nts = { ...ts };
                        if (nts.pin_type === 'icon') {
                            // Exclusivity for icon source
                            if (nts.pin_icon_key) {
                                nts.pin_icon_url = null;
                            } else if (nts.pin_icon_url) {
                                nts.pin_icon_key = null;
                            }
                            // Numbering not applicable for icons
                            nts.pin_number_mode = null;
                        } else if (nts.pin_type === 'numbered') {
                            nts.pin_icon_key = null;
                            nts.pin_icon_url = null;
                        }
                        return nts;
                    });
                }
                return nd;
            })
        };
        return ni;
    };

    const onSave = async (optional_new_pre_trip_item_data) => {
        setIsTouched(true);
        if (!newDocsAdded && !optional_new_pre_trip_item_data && (await validateRequiredTripConditions(trip, hasChanged, clientErrors, setClientErrors, enqueueSnackbar)) === false) {
            return;
        }
        if (await checkAdditionalCustomIds(trip, null, enqueueSnackbar) || (!!tourOperator?.config?.enable_pre_trip_items_visibility && checkTripItemsVisibility(trip.pre_trip_items, enqueueSnackbar))) {
            return;
        }
        if (clientErrors.hasEmptyCheckinOrBoardingSelection) {
            enqueueSnackbar('Please select categories.', { variant: 'error' });
            return;
        }

        let trip_data = { ...trip };
        trip_data.context = await removeUnusedTagsFromContextFields(trip.context) || {};

        if (trip.itinerary) {
            trip_data.itinerary = await removeTransportationToggle(trip.itinerary);
            trip_data.itinerary = await addTransportationToAllActivities(trip_data.itinerary);
            trip_data.itinerary = await removeEmptyActivities(trip_data.itinerary);
            trip_data.itinerary = await hideIfTimeLabelTextNull(trip_data.itinerary);
            // Remove UI-only fields such as _pin_* and timeslot.open before save
            trip_data.itinerary = stripUIFieldsFromItinerary(trip_data.itinerary);
            // Normalize map pin fields (icon vs number)
            trip_data.itinerary = normalizePinsInItinerary(trip_data.itinerary);
        }

        if (hasPerm('tour', 'update_tag_categories') && trip_data.tag_categories && Array.isArray(trip_data.tag_categories)) {
            // Filter out inherited operator default categories (they have non-numeric group_tour)
            // Only send trip-level categories to the backend
            trip_data.tag_categories = trip_data.tag_categories
                .filter(tc => typeof tc.group_tour === 'number')
                .sort((a, b) => {
                    const seqA = a.sequence || 0;
                    const seqB = b.sequence || 0;
                    return seqA - seqB;
                });
        } else {
            delete trip_data.tag_categories;
        }

        if (optional_new_pre_trip_item_data) {
            trip_data.pre_trip_items = [...optional_new_pre_trip_item_data];
        }

        // Ensure we always send current editable pre-check-in settings under pre_check_settings
        // (UI reads from pre_check_settings_own, but backend expects them in pre_check_settings)
        if (trip_data.pre_check_settings_own) {
            trip_data.pre_check_settings = { ...trip_data.pre_check_settings_own };
        }

        // Serialize vehicle_route_set FK to ID for the backend
        if (trip_data.vehicle_route_set && typeof trip_data.vehicle_route_set === 'object') {
            trip_data.vehicle_route_set = trip_data.vehicle_route_set.id;
        }

        // Remove read-only *_own fields from payload (backend expects own values under plain keys)
        delete trip_data.has_group_chat_own;
        delete trip_data.location_sharing_own;
        delete trip_data.tag_categories_own;
        delete trip_data.pre_check_settings_own;

        // UI-only snapshots of the stored row (reformatCustomIds); `managed` itself is sent.
        if (Array.isArray(trip_data.additional_custom_ids)) {
            trip_data.additional_custom_ids = trip_data.additional_custom_ids.map(({ _stored_managed, _stored_row, ...aeid }) => aeid);
        }

        setSyncActive(true);
        let res = await dataAPI(UPDATE, props.resource, { id: trip.id, data: trip_data, upload: trip._upload });
        if (res.statusCode === 200 || res.statusCode === 201) {
            enqueueSnackbar('Trip saved', { variant: 'success' });
            const mapped = mapOwnFieldsToUI(reformatCustomIds(res.data));
            setTrip(mapped);
            setOriginalState(getComparableState(mapped));
            setNewDocsAdded(false);
        } else if (res?.statusCode === 409) {
            // e.g. easy_go_live_required: nothing was saved. Say why and keep the user's edits (still unsaved).
            enqueueSnackbar(res.data?.message || res.data?.detail || 'Error saving trip', { variant: 'error' });
            setSyncActive(false);
            return;
        } else {
            if (res?.data?.message) {
                enqueueSnackbar(res.data.message, { variant: 'error' });
            } else {
                enqueueSnackbar('Error saving trip', { variant: 'error' });
            }
        }
        setSyncActive(false);
        setIsTouched(false);
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_cover_image':
                handleTripCoverImageConfirmed();
                break;
            case 'delete_trip':
            case 'delete_trips':
                doDeleteTrip();
                break;
            case 'delete_meeting_point_photo':
                handleMeetingPointPhotoDeletionConfirmed();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    // A confirmed delete leaves for the list via this flag rather than pushing directly, so
    // BlockRedirect has dropped its block by the time the navigation happens (its effect
    // cleanup runs before this effect does) and unsaved edits on a trip that no longer
    // exists don't raise the leave prompt.
    const [tripDeleted, setTripDeleted] = React.useState(false);
    React.useEffect(() => {
        if (tripDeleted) {
            history.push(`/trip`);
        }
    }, [tripDeleted, history]);

    const doDeleteTrip = () => {
        setSyncActive(true);
        dataAPI(DELETE_BATCH, 'tour', { data: { list: [trip.id] } })
            .then(() => {
                enqueueSnackbar('Trip deleted', { variant: 'default' });
                setSyncActive(false);
                setTripDeleted(true);
            })
            .catch(() => {
                enqueueSnackbar('Error deleting trip', { variant: 'error' });
            });
    };

    const onDeleteTripList = () => {
        setConfirmationDialogAction('delete_trip');
    };

    const handleTripCoverImageDelete = () => {
        setConfirmationDialogAction('delete_cover_image');
    };
    const handleMeetingPointPhotoDelete = () => {
        setConfirmationDialogAction('delete_meeting_point_photo');
    }

    const handleTripCoverImageConfirmed = () => {
        setTrip((trip) => ({
            ...trip,
            cover_image: null,
            _changed: true,
        }));
    };

    const handleMeetingPointPhotoDeletionConfirmed = () => {
        setTrip((trip) => ({
            ...trip,
            meeting_point: {
                ...trip.meeting_point,
                photo: null,
            },
            _changed: true,
        }));
    };

    const lastURLParamsString = React.useMemo(() => {
        if (!props.lastURLParams) return '';
        if (typeof props.lastURLParams === 'string') return props.lastURLParams;
        if (typeof props.lastURLParams === 'object' && typeof props.lastURLParams.toString === 'function') {
            return props.lastURLParams.toString();
        }
        return '';
    }, [props.lastURLParams]);

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(trip.tour_operator)}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <React.Fragment>
                {/* CP-8349: a guide group's dates are synthetic, so the title carries none. */}
                <PageMeta title={trip.is_staff_chat_group ? `Edit Trip - ${trip.name}` : `Edit Trip - ${trip.name} [${moment(trip.start_date).format('MMMM D, YYYY')}]`} />
                {/* CP-6658: only this trip's own tabs are free to switch. '/trip' also matched
                    the trip list, which is where the back arrow goes, so the prompt never fired. */}
                <BlockRedirect
                    shouldBlock={hasChanged && !tripDeleted && hasPerm('tour', 'write')}
                    message={'Trip: are you sure you want to leave without saving changes?'}
                    ignoredPaths={[`/trip/${urlParams.id}`]}
                />

                {confirmationDialogAction ? (
                    <ConfirmationDialog
                        classes={{ paper: classes.paper }}
                        keepMounted
                        onCancel={confirmationDialogCancel}
                        onConfirm={confirmationDialogConfirm}
                        title={ConfirmationDialogText[confirmationDialogAction].title}
                        okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                        cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                        open={!!confirmationDialogAction}
                    >
                        {ConfirmationDialogText[confirmationDialogAction].body}
                    </ConfirmationDialog>
                ) : null}
                <Dialog open={showDefaultsModal} onClose={() => setShowDefaultsModal(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Default Settings Not Set Up</DialogTitle>
                    <DialogContent>
                        <Typography gutterBottom>
                            This trip is configured to use Tour Operator default settings, but defaults are not set up yet.
                        </Typography>
                        <Typography>
                            You can set defaults now or disable &quot;Use Tour Operator default settings&quot; for this trip.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setTrip((prev) => ({ ...prev, use_operator_defaults: false, _changed: true }));
                            setShowDefaultsModal(false);
                        }}>Disable for this trip</Button>
                        <Button variant="contained" color="primary" onClick={() => {
                            setShowDefaultsModal(false);
                            history.push('/settings/general');
                        }}>Open Settings</Button>
                    </DialogActions>
                </Dialog>
                <FixedLinearProgress visibility={syncActive} />
                <Grid container direction="row" className={classes.gridRoot} style={{ height: urlParams.tab === 'users' && '100%' }}>
                    <NavigateBackBar label={trip.name} url={'/trip?' + lastURLParamsString} maxLabelLength={tripTitleLength} props />
                    <Grid item xs={12} className={classes.tabSelectButtons}>
                        <Tabs value={currentTab} variant='scrollable' scrollButtons="auto" onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                            {visibleTabs.map((tab, idx) => (
                                <Tab key={tab.key} label={tab.label} {...a11yProps(idx)} />
                            ))}
                        </Tabs>
                    </Grid>
                    <Grid item xs={12}>
                        <TabPanel value={currentTab} index={paramsToTab.info}>
                            <TripEditInfo
                                data={trip}
                                syncActive={syncActive}
                                onDeleteTripList={onDeleteTripList}
                                handleTripCoverImageDelete={handleTripCoverImageDelete}
                                handleMeetingPointPhotoDelete={handleMeetingPointPhotoDelete}
                                clientErrors={clientErrors}
                                setClientErrors={setClientErrors}
                                checkAdditionalCustomIds={checkAdditionalCustomIds}
                                onChange={onTripInfoChange}
                                onCrosssellsChange={onCrosssellsChange}
                                isSmallScreen={isSmallScreen}
                                isTouched={isTouched}
                            />
                        </TabPanel>
                        {paramsToTab.itinerary !== undefined && (
                            <TabPanel value={currentTab} index={paramsToTab.itinerary} idprefix={!isSmallScreen ? 'vertical-tab' : null} style={{ flexGrow: 1 }}>
                                <TripBuilder data={trip} setNewDocsAdded={setNewDocsAdded} newDocsAdded={newDocsAdded} setClientErrors={setClientErrors} onChange={onTripFlowChange} onTripUpdate={onTripInfoChange} onValidate={onValidate} />
                            </TabPanel>
                        )}
                        {paramsToTab.lineset !== undefined && (
                            <TabPanel value={currentTab} index={paramsToTab.lineset} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                                <TripLineSet data={trip} onChange={onTripInfoChange} />
                            </TabPanel>
                        )}
                        {paramsToTab.floorplans !== undefined && (
                            <TabPanel value={currentTab} index={paramsToTab.floorplans} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                                <TripFloorplans />
                            </TabPanel>
                        )}
                        {paramsToTab.users !== undefined && currentTab === paramsToTab.users &&
                            <UserManager setActivePopperState={setActivePopperState} trip={trip} data={trip.users} setTrip={setTrip} />
                        }
                        {paramsToTab.items !== undefined && (
                            <TabPanel value={currentTab} index={paramsToTab.items} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                                <TripEditItems onTripSave={onSave} data={trip} onChange={onTripFlowChange} onValidate={onValidate} setSyncActive={setSyncActive} />
                            </TabPanel>
                        )}
                        <TabPanel value={currentTab} index={paramsToTab.checkin} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                            <TripPreCheckSettings
                                data={trip}
                                onChange={onTripInfoChange}
                                hasWritePerm={hasPerm('tour', 'write')}
                                onSave={onSave}
                            />
                        </TabPanel>
                        {paramsToTab.chat !== undefined && (
                            <TabPanel value={currentTab} index={paramsToTab.chat} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                                <TripChatList trip={trip} data={trip.users} isSmallScreen={isSmallScreen} />
                            </TabPanel>
                        )}
                        {paramsToTab.changes !== undefined && (
                            <TabPanel value={currentTab} className={classes.RevisionHistoryDialogTabPanel} index={paramsToTab.changes} idprefix={!isSmallScreen ? 'vertical-tab' : null}>
                                <RevisionHistoryDialog open={currentTab === paramsToTab.changes} resource={props.resource} id={trip.id} />
                            </TabPanel>
                        )}
                    </Grid>
                </Grid>

                {(activePopperState && isSmallScreen) || currentTab === paramsToTab.users ? (
                    <></>
                ) : (
                    <Grid item xs={12} container className={classes.mainButtonsWrapper}>
                        {/* CP-8423: SAVE bottom-right. No CANCEL: SAVE covers the whole trip across
                            tabs, so a page-wide revert would be confusing. */}
                        <FormActionButtons
                            mode="edit"
                            onSubmit={() => onSave()}
                            submitDisabled={!hasChanged || !hasPerm('tour', 'write') || syncActive}
                            size="medium"
                            sx={{ flex: 1 }}
                        />
                    </Grid>
                )}
            </React.Fragment>
        </LocalizationProvider>
        </FormLicenseeContext.Provider>
    );
};

TripEditForm.propTypes = {
    data: PropTypes.object.isRequired,
    resource: PropTypes.string.isRequired,
    lastURLParams: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const TripEdit = (props) => {
    const resource = 'tour';
    const routeParams = useParams();
    const { hasPerm } = React.useContext(ContextStore);
    const id = routeParams.id;
    if (!hasPerm(resource, 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id} fullResponse>
            <RecordDetailsContext.Consumer>{(res) => (res.statusCode === 200 ? <TripEditForm {...props} resource={resource} id={id} data={res.data} /> : <TripLoading {...props} resource={resource} id={id} res={res} />)}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default TripEdit;
